import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import t from '../../lib/I18n';
import Icon from '../general/Icon';
import FormattedHTMLMessage from '../general/FormattedHTMLMessage';
import Button from 'react-bootstrap/lib/Button';
import { SignupTypes } from '../../constants/AppConstants';
import { PrivacyPolicyLink, TermsAndConditionsLink } from '../customer/SignupTAndCs';
import FeatureFlagStore from '../../stores/FeatureFlagStore';
import ConsentsForm from '../../records/customer/ConsentsForm';

export const useSubmitButtonDisabled = ({ isLastStep, isSmartcardStep, consentsForm, isOver16 }) => {
    const [isDisabled, setIsDisabled] = useState(false);

    useEffect(() => {
        if (isSmartcardStep) {
            const enableSmartcardRegistration = isOver16 ? true : FeatureFlagStore.get('enableChildSmartcardRegistration');
            setIsDisabled(!enableSmartcardRegistration);
        } else if (isLastStep) {
            if (FeatureFlagStore.get('enableMarketingConsents')) {
                const isConsentsFormComplete = consentsForm.isComplete();
                setIsDisabled(!isConsentsFormComplete);
            } else {
                setIsDisabled(false);
            }
        }
    }, [isLastStep, isSmartcardStep, consentsForm, isOver16]);

    return { isDisabled };
};

const RegisterModalFooter = ({
    handleContinueRegistration,
    handleSubmission,
    handleLogin,
    isFirstStep,
    isSmartcardStep,
    isLastStep,
    isDesktop,
    onSmartcardUpdate,
    isRegistering,
    consentsForm,
    isOver16,
}) => {
    const { isDisabled } = useSubmitButtonDisabled({ isLastStep, isSmartcardStep, consentsForm, isOver16 });
    const onContinueWithout = async () => {
        await onSmartcardUpdate('signup', SignupTypes.WITHOUT_SMARTCARD);
        handleContinueRegistration();
    };

    const handleSmartcardSubmission = async () => {
        await onSmartcardUpdate('signup', SignupTypes.WITH_SMARTCARD);
        handleContinueRegistration();
    };

    const smartcardLabel = isDesktop
        ? t('account.register.smartcard.labelLong')
        : t('account.register.smartcard.labelShort');

    return (
        <div
            className={classnames('modal-footer__wrapper', {
                'modal-footer__wrapper-smartcard': isSmartcardStep,
            })}
        >
            {isSmartcardStep && (
                <button
                    className="action-btn action-btn--secondary modal__footer__button modal__footer__button--secondary modal-footer__button modal-footer__button-secondary"
                    data-test-id="register-modal-skip-smartcard-button"
                    onClick={onContinueWithout}
                >
                    <span className="modal__footer__button__text">
                        {t('account.register.continueWithout')}
                    </span>
                    <Icon icon="chevronslim" className="modal__footer__button__arrow" />
                </button>
            )}

            {isLastStep && (
                <div
                    className={classnames('text-sm text-weight--semi-bold', {
                        'margin-bottom': isDesktop,
                        'margin-bottom--sm': !isDesktop,
                    })}
                >
                    <FormattedHTMLMessage
                        message="form.registration.termsAndConditions"
                        privacyPolicy={<PrivacyPolicyLink className="text-capitalise" />}
                        termsAndConditions={
                            <TermsAndConditionsLink
                                message={t('form.termsAndConditions.capitalised')}
                            />
                        }
                    />
                </div>
            )}

            <button
                className={classnames(
                    'action-btn action-btn--primary modal__footer__button modal__footer__button--primary modal-footer__button modal-footer__button-primary',
                    { 'modal__footer__button--processing': isRegistering },
                )}
                data-test-id="register-modal-submit-button"
                onClick={
                    isLastStep
                        ? handleSubmission
                        : isSmartcardStep
                        ? handleSmartcardSubmission
                        : handleContinueRegistration
                }
                disabled={isDisabled}
            >
                <span className="modal__footer__button__text">
                    {t(
                        isLastStep
                            ? 'account.register.create'
                            : isSmartcardStep
                            ? smartcardLabel
                            : 'account.register.continue',
                    )}
                </span>
                <Icon icon="chevronslim" className="modal__footer__button__arrow" />
            </button>

            {isFirstStep && isDesktop && (
                <div className="modal-footer__link">
                    {t('account.register.loginLabel')}{' '}
                    <Button className="like-link" onClick={handleLogin}>
                        {t('account.login')}
                    </Button>
                </div>
            )}

            {isLastStep && isDesktop && (
                <div className="modal-footer__privacy-notice">
                    {t('preference-centre.privacy-notice').length > 1 && (
                        <FormattedHTMLMessage
                            className="text-xs text-center"
                            message="preference-centre.privacy-notice"
                        />
                    )}
                    {t('preference-centre.privacy-notice.extra').length > 1 && (
                        <FormattedHTMLMessage
                            className="text-xs text-center"
                            message="preference-centre.privacy-notice.extra"
                        />
                    )}
                </div>
            )}
        </div>
    );
};

RegisterModalFooter.propTypes = {
    handleContinueRegistration: PropTypes.func,
    handleSubmission: PropTypes.func,
    handleLogin: PropTypes.func,
    isFirstStep: PropTypes.bool,
    isSmartcardStep: PropTypes.bool,
    isLastStep: PropTypes.bool,
    isDesktop: PropTypes.bool,
    onSmartcardUpdate: PropTypes.func,
    onClose: PropTypes.func,
    isRegistering: PropTypes.bool,
    consentsForm: PropTypes.instanceOf(ConsentsForm),
    isOver16: PropTypes.bool,
};

export default RegisterModalFooter;
